import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const SB_MONO = "https://customer-assets.emergentagent.com/job_af8f5b12-cd02-481d-8aac-658ba1fd8e83/artifacts/uzu8frqy_mono.png";
const EASE = [0.22, 1, 0.36, 1];

const TICKER = [
    "Product Engineering", "AI Systems", "SaaS Architecture", "MVPs that ship",
    "Internal Tools", "API Design", "Automation", "UX-driven", "Built in 2025",
];

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
    const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

    const bgRef = useRef(null);
    useEffect(() => {
        const onMove = (e) => {
            if (!bgRef.current) return;
            const x = (e.clientX / window.innerWidth - 0.5) * 18;
            const y = (e.clientY / window.innerHeight - 0.5) * 18;
            bgRef.current.style.transform = `translate3d(${x}px, ${y}px, 0)`;
        };
        window.addEventListener("mousemove", onMove);
        return () => window.removeEventListener("mousemove", onMove);
    }, []);

    return (
        <section
            ref={ref}
            id="hero"
            data-testid="hero-section"
            className="relative isolate min-h-[100svh] overflow-hidden"
        >
            {/* Layered background */}
            <div ref={bgRef} className="absolute inset-0 -z-10 transition-transform duration-300 ease-out">
                <div className="absolute inset-0 bg-dots opacity-60" />
                <div className="absolute inset-0 bg-arch-grid" />
                <OrbitalDiagram />
            </div>

            {/* Floating gradients */}
            <div className="pointer-events-none absolute -top-1/4 -left-1/4 h-[70vmax] w-[70vmax] mint-glow" style={{ opacity: 0.7 }} />
            <div className="pointer-events-none absolute -bottom-1/3 -right-1/4 h-[60vmax] w-[60vmax] rounded-full"
                 style={{ background: "radial-gradient(closest-side, rgb(63 191 147 / 0.06), transparent 70%)", filter: "blur(40px)" }} />

            {/* Vignette */}
            <div className="absolute inset-0 -z-10"
                 style={{ background: "radial-gradient(100% 70% at 50% 50%, transparent 30%, rgb(var(--sb-bg)) 95%)" }} />

            <motion.div
                style={{ y, opacity }}
                className="relative mx-auto flex min-h-[100svh] w-full max-w-[1480px] flex-col justify-center px-6 sm:px-12 md:px-20 pt-32 pb-28 md:pt-40"
            >
                {/* Overline */}
                <motion.div
                    initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: EASE, delay: 1.75 }}
                    className="mb-10 flex items-center gap-3"
                >
                    <span className="flex h-7 items-center gap-2 rounded-full border-sb border bg-sb-2/40 px-3 font-mono text-[10px] uppercase tracking-[0.22em] text-sb-mint">
                        <span className="h-1.5 w-1.5 rounded-full bg-sb-mint sb-pulse" />
                        Independent · Studio · Available
                    </span>
                    <span className="hidden md:inline font-mono text-[10px] uppercase tracking-[0.22em] text-sb-muted">
                        Vol. 01 · 2025
                    </span>
                </motion.div>

                {/* Editorial headline */}
                <h1
                    data-testid="hero-headline"
                    aria-label="Engineering intelligent digital products."
                    className="balance max-w-[19ch] text-[clamp(2.25rem,5.6vw,5.25rem)] font-medium leading-[1] tracking-[-0.03em]"
                >
                    <Word delay={1.85} text="Engineering" />{" "}
                    <Word delay={1.95} className="font-serif italic text-sb-mint pr-1" text="intelligent" />{" "}
                    <Word delay={2.05} text="digital" />{" "}
                    <Word delay={2.15} className="font-serif italic" text="products" />
                    <Word delay={2.2} text="." />
                </h1>

                <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
                    {/* Left: subhead */}
                    <motion.div
                        initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: EASE, delay: 2.4 }}
                        className="md:col-span-7"
                    >
                        <p
                            data-testid="hero-subheadline"
                            className="max-w-[58ch] text-balance text-base md:text-lg leading-relaxed text-sb-soft font-light"
                        >
                            <span className="text-sb">SysBrains</span> is a modern software product engineering studio.
                            We craft AI-powered systems, custom software, and refined internal tools.
                            <span className="font-serif italic text-sb"> built with clarity, precision, and intent.</span>
                        </p>

                        <div className="mt-10 flex flex-wrap items-center gap-5">
                            <a
                                href="#contact"
                                data-testid="hero-cta-button"
                                className="group relative inline-flex items-center gap-3 rounded-full bg-sb-mint pl-6 pr-2 py-2 text-sm font-medium text-[#0B1220] transition-all"
                                style={{ boxShadow: "0 18px 50px -16px rgb(var(--sb-mint) / 0.6)" }}
                            >
                                <span>Begin a conversation</span>
                                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#0B1220] text-sb-mint transition-transform group-hover:rotate-45">
                                    <svg width="14" height="14" viewBox="0 0 10 10" fill="none"><path d="M1 9L9 1M9 1H2M9 1V8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                </span>
                            </a>

                            <a
                                href="#capabilities"
                                data-testid="hero-secondary-link"
                                className="group inline-flex items-center gap-3 text-sm text-sb-soft hover:text-sb transition-colors"
                            >
                                <span className="font-mono uppercase tracking-[0.18em] text-[11px]">See the practice</span>
                                <span className="h-px w-8 bg-sb-muted transition-all group-hover:w-12 group-hover:bg-sb-mint" />
                            </a>
                        </div>
                    </motion.div>

                    {/* Right: meta card */}
                    <motion.aside
                        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: EASE, delay: 2.5 }}
                        className="md:col-span-5 md:col-start-9"
                    >
                        <div className="border-sb border-l pl-6 md:pl-8">
                            <div className="space-y-5">
                                {[
                                    { k: "Practice", v: "Product engineering studio" },
                                    { k: "Method", v: "Strategy · Architecture · Engineering" },
                                    { k: "Team", v: "Small · High-signal · Senior" },
                                    { k: "Mode", v: "Selective partnerships, 2025–" },
                                ].map((row, i) => (
                                    <div key={row.k} className="flex items-baseline gap-6">
                                        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-sb-muted w-20 shrink-0">
                                            {String(i + 1).padStart(2, "0")} · {row.k}
                                        </span>
                                        <span className="text-sm text-sb">{row.v}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </motion.aside>
                </div>

                {/* Bottom marquee */}
                <motion.div
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 2.7 }}
                    className="absolute inset-x-0 bottom-8 overflow-hidden"
                >
                    <div className="hairline mb-6 mx-6 sm:mx-12 md:mx-20" />
                    <div className="flex overflow-hidden">
                        <div className="marquee-track flex shrink-0 items-center gap-12 pr-12 font-mono text-[11px] uppercase tracking-[0.28em] text-sb-muted">
                            {[...TICKER, ...TICKER].map((t, i) => (
                                <span key={i} className="flex items-center gap-12">
                                    <span>{t}</span>
                                    <img src={SB_MONO} alt="" className="h-3 w-3 rounded-[3px] opacity-60" />
                                </span>
                            ))}
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </section>
    );
}

function Word({ text, delay = 0, className = "" }) {
    return (
        <motion.span
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay }}
            className={`inline-block ${className}`}
        >
            {text}
        </motion.span>
    );
}

function OrbitalDiagram() {
    // Lightweight architectural diagram in top-right
    return (
        <div className="absolute right-[-8vw] top-[6vh] hidden md:block text-sb-mint opacity-30">
            <svg width="640" height="640" viewBox="0 0 640 640" fill="none" aria-hidden>
                <defs>
                    <radialGradient id="rg" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="currentColor" stopOpacity="0.4" />
                        <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
                    </radialGradient>
                </defs>
                <g stroke="currentColor" strokeOpacity="0.4" strokeWidth="0.5" fill="none">
                    <circle cx="320" cy="320" r="80" />
                    <circle cx="320" cy="320" r="150" strokeDasharray="2 4" />
                    <circle cx="320" cy="320" r="220" strokeOpacity="0.18" />
                    <circle cx="320" cy="320" r="290" strokeOpacity="0.1" strokeDasharray="1 6" />
                </g>
                <g fill="currentColor">
                    <circle cx="320" cy="240" r="3" />
                    <circle cx="470" cy="320" r="2" />
                    <circle cx="232" cy="424" r="2.5" />
                    <circle cx="540" cy="220" r="1.8" opacity="0.7" />
                    <circle cx="100" cy="380" r="1.6" opacity="0.6" />
                </g>
                <circle cx="320" cy="320" r="50" fill="url(#rg)" />
            </svg>
        </div>
    );
}
